import { useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { useFines } from "../api/fines-api";
import { lockDocumentScroll } from "../lockDocumentScroll";
import { useSupabaseAuth } from "../supabase/SupabaseContext";
import { TabBar } from "./dss/TabBar";

// The persistent bottom tab bar (Kalender / Bøder / Lod / Admin-if-admin)
// around the tabbed destinations. Full-screen flows (auth, edit forms) are
// mounted outside this layout in App.tsx and render without it.
const AppShell = () => {
  const location = useLocation();
  const { hasRole } = useSupabaseAuth();
  const { data: fines } = useFines();
  const unpaidFinesCount = fines?.filter((f) => f.status === "unpaid").length ?? 0;
  // The shell is as tall as the screen and the engine thinks the viewport is
  // shorter, so the document is scrollable by the difference even though
  // nothing here wants it to be. Scrolling it takes the tab bar off the
  // bottom of the screen. Only while this is mounted: the full-screen forms
  // outside the shell scroll the document for real. See lockDocumentScroll.
  useEffect(lockDocumentScroll, []);

  const active = location.pathname.startsWith("/penalties")
    ? "boder"
    : location.pathname.startsWith("/finance")
      ? "finans"
      : location.pathname.startsWith("/lod")
        ? "lod"
        : location.pathname.startsWith("/edit")
          ? "admin"
          : "kalender";

  // The finance area is the one dark room in the app, so the shell around it
  // goes dark with it — otherwise the bone paper shows through wherever the
  // page doesn't paint: behind the tab bar, and in the strip under the notch
  // that the safe-area inset holds open. The document canvas behind all of
  // this is AreaChrome's job; the shell can only paint as far as its own box.
  const vault = active === "finans";

  return (
    // A fixed height (not min-height) is required here: min-height is a
    // floor, not a ceiling, so if the Outlet's content wants to be taller
    // than the viewport, the whole column would grow past it and the tab bar
    // would end up at the bottom of a page taller than the screen instead of
    // staying with the screen while the content underneath scrolls. That
    // height is `dss-app-shell`'s --app-height, which is the screen — not any
    // of the four readings that come back one status bar short of it once
    // installed. See index.css and appHeight.ts.
    //
    // The top inset is applied here rather than per-page. It's what keeps the
    // first row of content out from under the notch on a build where the web
    // view does run under it; box-sizing is border-box, so it eats into the
    // shell's own box instead of pushing the tab bar off the bottom of it.
    // Where the status bar reserves its own space instead — and on a plain
    // browser tab — it resolves to 0 and nothing changes.
    <div
      className={`dss-app-shell mx-auto max-w-md flex flex-col overflow-hidden ${
        vault ? "bg-vault" : "bg-paper"
      }`}
      style={{ paddingTop: "calc(env(safe-area-inset-top) + var(--view-as-offset))" }}
    >
      <div className="flex-1 flex flex-col min-h-0">
        <Outlet />
      </div>
      <TabBar
        active={active}
        admin={hasRole("ADMIN")}
        unpaidFinesCount={unpaidFinesCount}
        tone={vault ? "vault" : "paper"}
      />
    </div>
  );
};

export default AppShell;
